import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { usedeleteProductionMutation } from 'src/api/api.generated'
import { Typography, Stack } from '@vapor/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { useTheme } from '@mui/system'
import { useTenant } from 'src/app/hooks/context/context.hook'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { GraphQLError } from 'graphql'
import { HICLoading } from 'hic-components'
import useSuccessSnackBar from 'src/app/hooks/success-snack-bar/use-success-snack-bar'
import { ProductionAssignedReturn, UnionBill } from './production-assigned-actions.type'

type ProductionAssignedActionDeleteProps = {
  title: string
  open: boolean
  onClose: () => void
  bills: UnionBill[]
  productionAssignedIds: string[]
  getProductionAssigned: (bills: UnionBill[], productionAssignedId: string) => ProductionAssignedReturn
}

export function ProductionAssignedActionDelete({ title, open, onClose, bills, productionAssignedIds, getProductionAssigned }: ProductionAssignedActionDeleteProps) {
  const { t } = useTranslation()
  const { id: tenantId } = useTenant()
  const theme = useTheme()
  const showError = useErrorSnackBar()
  const showSuccessSnackBar = useSuccessSnackBar()

  const [deleteProductionMutation] = usedeleteProductionMutation()

  const [productionIds, setProductionIds] = useState<(string | undefined)[]>([])
  const [partialProductionAssigned, setPartialProductionAssigned] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const partialProductionAssignedList: string[] = []

    const relatedProduciton = productionAssignedIds.map((i) => {
      const pa = getProductionAssigned(bills, i)
      if (pa && pa.quota < 1) partialProductionAssignedList.push(pa.production.description ?? '')
      return pa?.production.id
    })
    setProductionIds(relatedProduciton)
    if (partialProductionAssignedList.length > 0) setPartialProductionAssigned(partialProductionAssignedList)
  }, [bills, productionAssignedIds])

  const deleteProduction = useCallback(async () => {
    const result: (readonly GraphQLError[])[] = []
    setLoading(true)
    await Promise.all(
      productionIds.map(async (id) => {
        if (id) {
          const singleDeleteResult = await deleteProductionMutation({
            variables: {
              tenantId: tenantId,
              id: id,
            },
            refetchQueries: ['ReservationDetail'],
          })
          singleDeleteResult.data?.deleteProduction
          if (singleDeleteResult.errors) result.push(singleDeleteResult.errors)
        }
        return
      }),
    )
    setLoading(false)
    if (result.length > 0) showError(result.join(';'))
    else {
      showSuccessSnackBar(t('pms.booking.passerby-bill.creation-dialog.update_success'))
      onClose()
    }
  }, [productionIds, tenantId])

  return (
    <FormLayout //
      title={title}
      appearance={{ type: 'DIALOG', open: open, onClose: onClose }}
      toolbar={
        <FormLayoutDefaultToolbar //
          deleteButton={{
            clickHandler: () => {
              deleteProduction()
            },
            variant: 'outlined',
          }}
          cancelButton={{ clickHandler: onClose, variant: 'contained' }}
        />
      }
    >
      {loading && <HICLoading />}
      <Stack alignContent="start" minWidth={300} minHeight={200}>
        <Typography variant="body">{t('pms.booking.reservation.productions_assigned.action.delete.message')}</Typography>
        <br />
        {partialProductionAssigned.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="body">{t('pms.booking.reservation.productions_assigned.action.delete.warning_message')}</Typography>
            {partialProductionAssigned.map((i, key) => (
              <Stack key={key} direction="row" spacing={1} alignItems="center">
                <FontAwesomeIcon icon={faTriangleExclamation} color={theme.palette.warning.main} />
                <Typography variant="caption"> {i}</Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </FormLayout>
  )
}
